import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  appTitle,
  adnPageSize,
  getFollowUpEventForOptions,
  FollowUpEventFor,
  ContractType
} from "../common/GeneralConfigurations";
import AdnMessageError from "../common/adnMessage/AdnMessageError";
import AdnStatus from "../common/adnStatus/AdnStatus";
import FollowUpEventTypeListForm from "./FollowUpEventTypeListForm";
import { Popconfirm, Space } from "antd";
import {
  getFollowUpEventList,
  updateFollowUpEvent,
} from "../../api/FollowUpEventApi";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

const ManageFollowUpEventTypeListPage = () => {
  const { t } = useTranslation();
  const title = t("followUpEventType.title");

  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [orderBy, setOrderBy] = useState("");
  const [isFor, setIsFor] = useState();
  const [isFirstYear, setFirstYear] = useState("");
  const [status, setStatus] = useState("false");
  const [contractTypeId, setContractTypeId] = useState("");
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [id, setId] = useState(null);

  useEffect(() => {
    document.title = `${appTitle} - ${title}`;
  }, [title]);

  const loadEventTypes = useCallback(() => {
    getFollowUpEventList(
      currentPage,
      adnPageSize,
      orderBy,
      isFor,
      isFirstYear,
      status,
      contractTypeId
    )
      .then((res) => {
        if (res.data.result !== null) {
          setEventTypes(res.data.result);
          setTotalRecords(res.data.totalRecords);
        } else {
          setEventTypes([]);
        }
        setLoading(false);
      })
      .catch((err) => console.log(err));
  }, [currentPage, orderBy, isFor, isFirstYear, status, contractTypeId]);

  useEffect(() => {
    loadEventTypes();
  }, [loadEventTypes]);

  const handleBackDeactivateModal = (value) => {
    setShowDeactivateModal(false);
  };

  const handleDeactivate = (endDate) => {
    let date = moment(endDate, "DD-MM-YYYY");
    handleUpdateFollowUpEvent(id, date.format("YYYY-MM-DD"));
  };

  const handleStatusChange = async (id, isActivating) => {
    setId(id);
    if (isActivating) {
      handleUpdateFollowUpEvent(id, null);
    } else {
      setShowDeactivateModal(true);
    }
  };

  const handleUpdateFollowUpEvent = (id, endDate) => {
    updateFollowUpEvent({ id: id, endDate: endDate })
      .then(async (res) => {
        if (res.status === 200) {
          loadEventTypes();
        } else {
          const errors = res.data.errors;
          errors.forEach((error) => {
            const errorMessage = error.message;
            AdnMessageError(errorMessage);
          });
        }
      })
      .catch((err) => AdnMessageError())
      .finally(() => setShowDeactivateModal(false));
  };

  const handleTableChanges = async (pagination, filters, sorter, extra) => {
    sorter.order === "descend"
      ? setOrderBy("-" + sorter.field)
      : setOrderBy(sorter.field);
  };

  const academicHandleChange = (value) => {
    setCurrentPage(1);
    if (value !== undefined) {
      setIsFor(value);
    } else {
      setIsFor(undefined);
    }
  };

  const firstYearHandleChange = (value) => {
    setCurrentPage(1);
    if (value !== undefined) {
      setFirstYear(value);
    } else {
      setFirstYear(undefined);
    }
  };

  const statusHandleChange = (value) => {
    setCurrentPage(1);
    if (value !== undefined) {
      setStatus(value);
    } else {
      setStatus("");
    }
  };

    const contractTypeHandleChange = (value) => {
      debugger
      setCurrentPage(1);
      if (value !== undefined) {
          setContractTypeId(value);
      } else {
          setContractTypeId(undefined);
      }
  };

  const statusOptions = [
    { id: true, name: t("followUpEventType.status.inactive") },
    { id: false, name: t("followUpEventType.status.active") },
  ];

  const yesOrNoOptions = [
    { id: true, name: t("followUpEventType.options.yes") },
    { id: false, name: t("followUpEventType.options.no") },
  ];

  const contractTypeOptions = [
    { id: ContractType.CTI, name: "CTI" },
    { id: ContractType.CPS, name: "CPS" },
    { id: ContractType.CTC, name: "CTC" },
    { id: ContractType.Academy, name: "Academy" }
  ];


    const columns = [
    {
      title: t("followUpEventType.columns.contractTypeName"),
      dataIndex: "contractTypeName",
      key: "contractTypeName",
      width: "10%",
      render(text, record) {
          return {
              props: {
                  style: {
                      textAlign: "center",
                  },
              },
              children: <div> {text ? text.split(" ")[0] : <></>}</div>,
          };
      },
    },
    {
      title: t("followUpEventType.columns.name"),
      dataIndex: "name",
      key: "name",
      width: "40%",
    },
    {
      title: t("followUpEventType.columns.doneAfter"),
      dataIndex: "month",
      key: "month",
      width: "10%",
      render: (text) => <>{text + " " + t("followUpEventType.labels.months")}</>,
    },
    {
      title: t("followUpEventType.columns.firstYearOnly"),
      dataIndex: "isFirstYear",
      key: "isFirstYear",
      width: "8%",
      render: (text, record) => (record.isFirstYear ? <CheckOutlined /> : ""),
    },
    {
      title: t("followUpEventType.columns.isFor"),
      dataIndex: "isFor",
      key: "isFor",
      width: "8%",
      render: (text, record) =>
        Object.keys(FollowUpEventFor).find(
          (key) => FollowUpEventFor[key] === record.isFor
        ),
    },
    {
      title: t("followUpEventType.columns.status"),
      dataIndex: "deleted",
      key: "deleted",
      width: "8%",
      render: (text, record) => <AdnStatus deleted={record.deleted} />,
    },
    {
      title: t("followUpEventType.columns.from"),
      dataIndex: "startDate",
      key: "startDate",
      width: "10%",
      render: (text) => moment(text).format("DD-MM-yyyy"),
    },
    {
      title: t("followUpEventType.columns.to"),
      dataIndex: "endDate",
      key: "endDate",
      width: "10%",
      render: (text) =>
        text == null ? null : moment(text).format("DD-MM-yyyy"),
    },
    {
      title: t("followUpEventType.columns.action"),
      key: "action",
      render: (record) => (
        <Space style={{ display: "flex", justifyContent: "center" }}>
          {record.deleted ? (
            <Popconfirm
              okText={t("followUpEventType.buttons.yes")}
              cancelText={t("followUpEventType.buttons.no")}
              okButtonProps={{
                style: { backgroundColor: "red", borderColor: "red" },
              }}
              title={t("followUpEventType.confirmations.activateEvent")}
              onConfirm={() => handleStatusChange(record.id, true)}
            >
              <CheckOutlined style={{ color: "green" }} />
            </Popconfirm>
          ) : (
            <Popconfirm
              okText={t("followUpEventType.buttons.yes")}
              cancelText={t("followUpEventType.buttons.no")}
              okButtonProps={{
                style: { backgroundColor: "red", borderColor: "red" },
              }}
              title={t("followUpEventType.confirmations.deactivateEvent")}
              onConfirm={() => handleStatusChange(record.id, false)}
            >
              <CloseOutlined style={{ color: "red" }} />
            </Popconfirm>
          )}
        </Space>
      ),
      width: "5%",
    },
  ];

  return (
    <FollowUpEventTypeListForm
      eventTypes={eventTypes}
      title={title}
      currentPage={currentPage}
      totalRecords={totalRecords}
      handleTableChanges={handleTableChanges}
      handleChangePage={setCurrentPage}
      loading={loading}
      columns={columns}
      statusOptions={statusOptions}
      contractTypeOptions={contractTypeOptions}
      isForOptions={getFollowUpEventForOptions()}
      yesOrNoOptions={yesOrNoOptions}
      academicHandleChange={academicHandleChange}
      firstYearHandleChange={firstYearHandleChange}
      statusHandleChange={statusHandleChange}
      contractTypeHandleChange={contractTypeHandleChange}
      showDeactivateModal={showDeactivateModal}
      handleDeactivate={handleDeactivate}
      handleBackDeactivateModal={handleBackDeactivateModal}
    />
  );
};

export default ManageFollowUpEventTypeListPage;
